import { useEffect, useState, useCallback, useRef } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { LogOut, RefreshCw, Loader2, Phone, Clock, ChevronRight, Store, Bike, MapPin, Printer, Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { spiceLabel } from "../components/VariantPicker";
import PrinterHealthWidget from "../components/PrinterHealthWidget";
import SpeakerPicker from "../components/SpeakerPicker";
import useDingPlayer from "../hooks/useDingPlayer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rivaaz_admin_token";

const STATUS_FLOW = ["new", "preparing", "ready", "completed"];

const STATUS_STYLES = {
  new: "bg-[#C9853E]/10 text-[#C9853E] border-[#C9853E]/30",
  preparing: "bg-[#E5B07D]/15 text-[#E5B07D] border-[#E5B07D]/30",
  ready: "bg-[#374F3B]/10 text-[#374F3B] border-[#374F3B]/30",
  completed: "bg-[#0E0E1A]/10 text-[#0E0E1A] border-[#0E0E1A]/30",
  cancelled: "bg-gray-200 text-gray-600 border-gray-300",
};

const PAYMENT_STYLES = {
  paid: "bg-green-100 text-green-700",
  pending: "bg-yellow-100 text-yellow-700",
  pay_at_pickup: "bg-blue-100 text-blue-700",
};

export default function Admin() {
  const navigate = useNavigate();
  const token = localStorage.getItem(TOKEN_KEY);
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [soundOn, setSoundOn] = useState(() => {
    const saved = localStorage.getItem("rivaaz_admin_sound");
    return saved === null ? true : saved === "1";
  });
  const knownIds = useRef(null); // null = first load, Set after

  // Notification-ding player. HTMLAudio-based (follows OS default output +
  // exposes setSinkId so the owner can pin the sound to their external
  // speakers). See /app/frontend/src/hooks/useDingPlayer.js for details.
  const {
    playDing,
    testDing,
    devices: audioOutputDevices,
    selectedDeviceId,
    selectDevice,
    canPickDevice,
    needsPermission: deviceLabelsLocked,
    requestPermission: unlockDeviceLabels,
  } = useDingPlayer(soundOn);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // ALWAYS fetch every order (no server-side status filter). Client-side
      // filters run separately. This is critical so the polling loop can
      // detect brand-new orders even when the operator is viewing the
      // "Preparing" or "Ready" tab — otherwise new orders never appear in
      // the polled data and the ding never fires.
      const { data } = await axios.get(`${API}/admin/orders`, { headers });
      // Detect brand-new orders (only after the first successful load)
      if (knownIds.current) {
        const fresh = data
          .filter((o) => o.status === "new" && !knownIds.current.has(o.id));
        if (fresh.length > 0) {
          playDing();
          toast.success(
            fresh.length === 1
              ? `🛎 New order from ${fresh[0].customer_name}`
              : `🛎 ${fresh.length} new orders`,
            { duration: 6000 }
          );
        }
      }
      knownIds.current = new Set(data.map((o) => o.id));
      setOrders(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        navigate("/admin/login");
        return;
      }
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [token, navigate, playDing]);

  useEffect(() => {
    if (!token) return;
    load();
    const interval = setInterval(load, 15000); // auto-refresh every 15s
    return () => clearInterval(interval);
  }, [load, token]);

  if (!token) return <Navigate to="/admin/login" replace />;

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem("rivaaz_admin_email");
    navigate("/admin/login");
  };

  const advance = async (order) => {
    const idx = STATUS_FLOW.indexOf(order.status);
    if (idx < 0 || idx >= STATUS_FLOW.length - 1) return;
    const next = STATUS_FLOW[idx + 1];
    setUpdating(order.id);
    try {
      const { data } = await axios.patch(
        `${API}/admin/orders/${order.id}/status`,
        { status: next },
        { headers }
      );
      setOrders((prev) => prev.map((o) => (o.id === data.id ? data : o)));
      toast.success(`Marked as ${next}`);
    } catch {
      toast.error("Failed to update status");
    } finally {
      setUpdating(null);
    }
  };

  const cancel = async (order) => {
    setUpdating(order.id);
    try {
      const { data } = await axios.patch(
        `${API}/admin/orders/${order.id}/status`,
        { status: "cancelled" },
        { headers }
      );
      setOrders((prev) => prev.map((o) => (o.id === data.id ? data : o)));
      toast("Order cancelled");
    } catch {
      toast.error("Failed to cancel");
    } finally {
      setUpdating(null);
    }
  };

  const reprint = async (order) => {
    try {
      await axios.post(`${API}/admin/orders/${order.id}/reprint`, {}, { headers });
      toast.success("Sent to printer queue");
    } catch (err) {
      toast.error("Failed to queue print");
    }
  };

  const counts = orders.reduce((acc, o) => {
    acc[o.status] = (acc[o.status] || 0) + 1;
    return acc;
  }, {});

  // Apply date-range filter on top of the already-loaded orders. Done
  // client-side because the admin already polls every 15s and the
  // 90-day TTL keeps the dataset small (< few thousand orders).
  const dateFilteredOrders = (() => {
    if (dateFilter === "all") return orders;
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startOfYesterday = startOfToday - 24 * 60 * 60 * 1000;
    const startOf7Days = startOfToday - 6 * 24 * 60 * 60 * 1000; // today + 6 prior days
    const startOf30Days = startOfToday - 29 * 24 * 60 * 60 * 1000;
    return orders.filter((o) => {
      const t = new Date(o.created_at).getTime();
      if (dateFilter === "today") return t >= startOfToday;
      if (dateFilter === "yesterday") return t >= startOfYesterday && t < startOfToday;
      if (dateFilter === "7days") return t >= startOf7Days;
      if (dateFilter === "30days") return t >= startOf30Days;
      return true;
    });
  })();
  const filteredOrders =
    filter === "all" ? dateFilteredOrders : dateFilteredOrders.filter((o) => o.status === filter);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20" data-testid="admin-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-10">
        <div>
          <div className="text-xs tracking-[0.25em] uppercase text-[#C9853E] font-bold mb-2">
            Staff dashboard
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold text-[#0E0E1A]">
            Live orders
          </h1>
          <p className="text-[#57534E] mt-2 text-sm">
            Auto-refreshing every 15 seconds · Signed in as{" "}
            <strong className="text-[#0E0E1A]">
              {localStorage.getItem("rivaaz_admin_email")}
            </strong>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/admin/menu"
            data-testid="admin-menu-link"
            className="inline-flex items-center gap-2 bg-white border border-[#E7E5E4] hover:border-[#0E0E1A] text-[#0E0E1A] rounded-full px-4 py-2.5 font-semibold text-sm"
          >
            Menu editor
          </Link>
          <Link
            to="/admin/home"
            data-testid="admin-home-link"
            className="inline-flex items-center gap-2 bg-white border border-[#E7E5E4] hover:border-[#0E0E1A] text-[#0E0E1A] rounded-full px-4 py-2.5 font-semibold text-sm"
          >
            Home photos
          </Link>
          <Link
            to="/admin/print"
            data-testid="admin-print-link"
            className="inline-flex items-center gap-2 bg-white border border-[#E7E5E4] hover:border-[#0E0E1A] text-[#0E0E1A] rounded-full px-4 py-2.5 font-semibold text-sm"
          >
            <Printer className="w-4 h-4" /> Printer
          </Link>
          <button
            data-testid="admin-sound-toggle"
            onClick={() => {
              const next = !soundOn;
              setSoundOn(next);
              localStorage.setItem("rivaaz_admin_sound", next ? "1" : "0");
              if (next) {
                playDing(); // also unlocks the WebAudio context on first click
                toast.success("New-order alerts on");
              } else {
                toast.info("Alerts muted");
              }
            }}
            title={soundOn ? "Mute new-order alerts" : "Enable new-order alerts"}
            className={`inline-flex items-center gap-2 border rounded-full px-4 py-2.5 font-semibold text-sm transition-all ${
              soundOn
                ? "bg-[#C9853E]/10 border-[#C9853E] text-[#A6692D]"
                : "bg-white border-[#E7E5E4] text-[#57534E] hover:border-[#0E0E1A]"
            }`}
          >
            {soundOn ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
            {soundOn ? "Alerts on" : "Muted"}
          </button>
          <button
            data-testid="admin-sound-test"
            onClick={() => {
              if (!soundOn) {
                setSoundOn(true);
                localStorage.setItem("rivaaz_admin_sound", "1");
              }
              testDing();
              toast("🔊 Test ding sent", { duration: 2500 });
            }}
            title="Play a test ding right now"
            className="inline-flex items-center gap-2 bg-white border border-[#E7E5E4] hover:border-[#0E0E1A] text-[#0E0E1A] rounded-full px-4 py-2.5 font-semibold text-sm"
          >
            🔊 Test
          </button>
          {canPickDevice && (
            <SpeakerPicker
              devices={audioOutputDevices}
              selectedDeviceId={selectedDeviceId}
              onSelect={async (id) => {
                // AWAIT the sink change so the confirmation ding
                // definitely plays through the newly-selected device
                // (not the previous sink).
                await selectDevice(id);
                toast.success("Speaker output updated");
                testDing();
              }}
              labelsLocked={deviceLabelsLocked}
              onUnlockLabels={unlockDeviceLabels}
            />
          )}
          <button
            data-testid="admin-refresh-btn"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 bg-white border border-[#E7E5E4] hover:border-[#0E0E1A] text-[#0E0E1A] rounded-full px-4 py-2.5 font-semibold text-sm disabled:opacity-60"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="inline-flex items-center gap-2 bg-[#0E0E1A] hover:bg-[#1C1B2B] text-white rounded-full px-4 py-2.5 font-semibold text-sm"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </div>

      {/* Printer health widget — one-glance status of the kitchen printer */}
      <div className="mb-6">
        <PrinterHealthWidget token={token} />
      </div>

      {/* Date range filter */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-3" data-testid="admin-date-filter">
        {[
          { v: "all", label: "All time" },
          { v: "today", label: "Today" },
          { v: "yesterday", label: "Yesterday" },
          { v: "7days", label: "Last 7 days" },
          { v: "30days", label: "Last 30 days" },
        ].map((d) => (
          <button
            key={d.v}
            data-testid={`admin-date-${d.v}`}
            onClick={() => setDateFilter(d.v)}
            className={`whitespace-nowrap px-3.5 py-1.5 rounded-full text-[11px] tracking-wider uppercase font-bold transition-all ${
              dateFilter === d.v
                ? "bg-[#C9853E] text-white"
                : "bg-white border border-[#E7E5E4] text-[#57534E] hover:border-[#C9853E] hover:text-[#C9853E]"
            }`}
          >
            {d.label}
          </button>
        ))}
      </div>

      {/* Filter pills */}
      <div className="flex gap-2 overflow-x-auto pb-3 mb-6" data-testid="admin-status-filter">
        {[
          { v: "all", label: "All", count: orders.length },
          { v: "new", label: "New", count: counts.new || 0 },
          { v: "preparing", label: "Preparing", count: counts.preparing || 0 },
          { v: "ready", label: "Ready", count: counts.ready || 0 },
          { v: "completed", label: "Completed", count: counts.completed || 0 },
          { v: "cancelled", label: "Cancelled", count: counts.cancelled || 0 },
        ].map((f) => (
          <button
            key={f.v}
            data-testid={`admin-filter-${f.v}`}
            onClick={() => setFilter(f.v)}
            className={`whitespace-nowrap px-4 py-2 rounded-full text-xs font-bold transition-all ${
              filter === f.v
                ? "bg-[#0E0E1A] text-white"
                : "bg-white border border-[#E7E5E4] text-[#0E0E1A] hover:border-[#0E0E1A]"
            }`}
          >
            {f.label} <span className="ml-1 opacity-60">{f.count}</span>
          </button>
        ))}
      </div>

      {loading && orders.length === 0 ? (
        <div className="flex items-center justify-center py-20 text-[#57534E]">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading orders…
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="bg-white border border-[#E7E5E4] rounded-3xl p-12 text-center" data-testid="admin-empty">
          <div className="font-display text-2xl font-bold text-[#0E0E1A]">
            {dateFilter === "all" ? "No orders yet" : "No orders in this date range"}
          </div>
          <p className="text-[#57534E] mt-2">
            {dateFilter === "all"
              ? "New orders will appear here automatically."
              : "Try a wider date range, or pick a different status filter."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="admin-orders-list">
          {filteredOrders.map((o) => {
            const next = STATUS_FLOW[STATUS_FLOW.indexOf(o.status) + 1];
            return (
              <article
                key={o.id}
                data-testid={`admin-order-${o.id}`}
                className="bg-white border border-[#E7E5E4] rounded-3xl p-6 flex flex-col"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <div className="font-display text-xl font-extrabold text-[#0E0E1A]">
                    #{o.id.slice(0, 8)}
                  </div>
                  <div className="text-xs text-[#57534E] inline-flex items-center gap-1 text-right">
                    <Clock className="w-3 h-3" />
                    <span className="flex flex-col items-end leading-tight">
                      <span data-testid={`order-date-${o.id.slice(0,8)}`}>
                        {new Date(o.created_at).toLocaleDateString("en-CA", { timeZone: "America/Edmonton", month: "short", day: "numeric", year: "numeric" })}
                      </span>
                      <span data-testid={`order-time-${o.id.slice(0,8)}`} className="text-[#0E0E1A] font-bold">
                        {new Date(o.created_at).toLocaleTimeString("en-CA", { timeZone: "America/Edmonton", hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </span>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span
                    className={`text-[10px] tracking-[0.18em] uppercase font-bold border rounded-full px-2.5 py-1 ${
                      STATUS_STYLES[o.status] || STATUS_STYLES.new
                    }`}
                  >
                    {o.status}
                  </span>
                  <span
                    className={`text-[10px] tracking-[0.18em] uppercase font-bold rounded-full px-2.5 py-1 ${
                      PAYMENT_STYLES[o.payment_status] || PAYMENT_STYLES.pending
                    }`}
                  >
                    {o.payment_status.replace(/_/g, " ")}
                  </span>
                  <span
                    className={`text-[10px] tracking-[0.18em] uppercase font-bold rounded-full px-2.5 py-1 inline-flex items-center gap-1 ${
                      o.fulfillment === "delivery"
                        ? "bg-[#C9853E]/15 text-[#A6692D]"
                        : "bg-[#0E0E1A]/10 text-[#0E0E1A]"
                    }`}
                    data-testid={`admin-fulfillment-${o.id}`}
                  >
                    {o.fulfillment === "delivery" ? <Bike className="w-2.5 h-2.5" /> : <Store className="w-2.5 h-2.5" />}
                    {o.fulfillment || "pickup"}
                  </span>
                </div>

                <div className="mt-4 text-sm">
                  <div className="font-bold text-[#0E0E1A]">{o.customer_name}</div>
                  <a
                    href={`tel:${o.customer_phone}`}
                    className="inline-flex items-center gap-1.5 text-[#57534E] hover:text-[#C9853E] mt-0.5"
                  >
                    <Phone className="w-3 h-3" /> {o.customer_phone}
                  </a>
                  {o.fulfillment === "delivery" && o.delivery_address ? (
                    <div className="text-xs text-[#57534E] mt-1.5 bg-[#FAF8F5] rounded-lg px-3 py-2" data-testid={`admin-delivery-${o.id}`}>
                      <div className="flex items-start gap-1.5">
                        <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0 text-[#C9853E]" />
                        <div>
                          <div className="font-bold text-[#0E0E1A]">
                            {o.delivery_address.street}
                            {o.delivery_address.unit && `, ${o.delivery_address.unit}`}
                          </div>
                          <div>
                            {o.delivery_address.city}, {o.delivery_address.postal_code}
                          </div>
                          {o.delivery_zone && (
                            <div className="opacity-70">Zone: {o.delivery_zone} · ${(o.delivery_fee || 0).toFixed(2)}</div>
                          )}
                          {o.delivery_address.instructions && (
                            <div className="italic mt-1">"{o.delivery_address.instructions}"</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-[#57534E] mt-1">Pickup: {o.pickup_time}</div>
                  )}
                  {o.notes && (
                    <div className="text-xs italic text-[#57534E] mt-2 bg-[#FAF8F5] rounded-lg px-3 py-2">
                      "{o.notes}"
                    </div>
                  )}
                </div>

                <ul className="mt-4 divide-y divide-[#E7E5E4] text-sm">
                  {o.items.map((it, i) => (
                    <li key={i} className="py-1.5 flex items-baseline justify-between gap-2">
                      <span className="text-[#0E0E1A]">
                        <strong>{it.quantity}×</strong> {it.name}
                        {it.variant && (
                          <span className="text-[#57534E]"> ({it.variant})</span>
                        )}
                        {it.spice_level && (
                          <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wider text-[#C9853E] bg-[#C9853E]/10 px-1.5 py-0.5 rounded">
                            🌶 {spiceLabel(it.spice_level)}
                          </span>
                        )}
                      </span>
                      <span className="text-[#57534E] text-xs whitespace-nowrap">
                        ${(it.price * it.quantity).toFixed(2)}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="mt-4 pt-4 border-t border-[#E7E5E4] flex items-baseline justify-between">
                  <span className="text-xs text-[#57534E]">Total</span>
                  <span className="font-display text-2xl font-extrabold text-[#0E0E1A]">
                    ${o.subtotal.toFixed(2)}
                  </span>
                </div>

                <div className="mt-4 flex gap-2">
                  {next && o.status !== "cancelled" && (
                    <button
                      data-testid={`admin-advance-${o.id}`}
                      onClick={() => advance(o)}
                      disabled={updating === o.id}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#C9853E] hover:bg-[#A6692D] disabled:opacity-60 text-white rounded-full px-4 py-2.5 text-xs font-bold"
                    >
                      Mark as {next} <ChevronRight className="w-3 h-3" />
                    </button>
                  )}
                  <button
                    data-testid={`admin-reprint-${o.id}`}
                    onClick={() => reprint(o)}
                    title="Send to kitchen printer"
                    className="inline-flex items-center justify-center gap-1.5 bg-white border border-[#E7E5E4] hover:border-[#0E0E1A] hover:text-[#0E0E1A] text-[#57534E] rounded-full px-3 py-2.5 text-xs font-bold"
                  >
                    <Printer className="w-3.5 h-3.5" />
                  </button>
                  {o.status !== "completed" && o.status !== "cancelled" && (
                    <button
                      data-testid={`admin-cancel-${o.id}`}
                      onClick={() => cancel(o)}
                      disabled={updating === o.id}
                      className="bg-white border border-[#E7E5E4] hover:border-[#C9853E] hover:text-[#C9853E] text-[#57534E] rounded-full px-4 py-2.5 text-xs font-bold"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
